import { MoreTimeOutlined as MoreTimeOutlinedIcon } from 'components/icons';
import { OFF_OUTLINED_CLASS } from 'components/buttons/buttonStyle';
import PermissionButton from 'components/buttons/PermissionButton';
import EmptyState from 'components/EmptyState';
import InfoTooltip from 'components/InfoTooltip';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import LoadMoreButton from 'components/tables/LoadMoreButton';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import Unmasked from 'components/Unmasked';
import { formatToHours } from 'components/util/HoursDisplay';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth';
import { useLoadMoreFeed } from 'hooks/useLoadMoreFeed';
import BlockedTag from 'pages/timecards/buttons/BlockedTag';
import { ClosedCaseDraftMarker, isOwnDraftOnClosedCase } from 'pages/timecards/list/ClosedCaseDraftMarker';
import TimecardList from 'pages/timecards/TimecardList';
import { useOpenFinishTimecard } from 'pages/timecards/useOpenFinishTimecard';
import { finishBlockerLines } from 'pages/timecards/utils/timecardUtils';
import { FC, useCallback, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetTimecardsWithFiltersQuery } from 'store/api/timecards';
import { MatterStateType } from 'types/matters';
import { TimecardListItemData } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { GridLoader, HomeShowMore } from './CasesTab';
import TabContentHeader from './TabContentHeader.tsx';

const PAGE_SIZE = 8;

export const myDraftTimecardsFilter = (userId: number | undefined) =>
    `createdByEmployee.id==(${userId});timecardStatusTypeCode=in=(DRAFT)`;

const CLOSED_MATTER_STATES: string[] = [
    MatterStateType.COMPLETED,
    MatterStateType.OFFER_LOST,
    MatterStateType.OFFER_WON,
];

/**
 * Home's row: the case's title, then the date, hours and case number, and Finish where the timecard list offers it,
 * off while the draft has finish blockers, which its tooltip and its description list; off, it stays a Tab stop, so
 * the list opens on focus as on hover. Where Finish is not offered, the row says why, as the list does: the case is
 * blocked, or closed.
 */
const HomeDraftRow = ({ timecard, userId }: { timecard: TimecardListItemData; userId: number | undefined }) => {
    const { openFinish, isFinishing, finishBlockersOf } = useOpenFinishTimecard();
    const { displayDecimalScale } = useTimecardPrecision();
    const { matter } = timecard;
    const isCaseClosed = CLOSED_MATTER_STATES.includes(matter.matterStateTypeCode);
    const canFinish = userId === timecard.employee.id && !matter.isCurrentlyBlocked && !isCaseClosed;
    // The leading marker already explains a closed case to the person who entered the draft.
    const showsClosedCaseInfo = isCaseClosed && !isOwnDraftOnClosedCase(timecard, userId);

    const reason = finishBlockerLines(finishBlockersOf(timecard)).join('\n');
    const reasonId = useId();
    const finish = (
        <Button
            variant="outline"
            size="sm"
            className={cn('border-grey-300 text-meta h-7 rounded-[6px] shadow-none', OFF_OUTLINED_CLASS)}
            disabled={!!reason || isFinishing}
            focusableWhenDisabled
            aria-describedby={reason ? reasonId : undefined}
            onClick={() => {
                void openFinish(timecard.id);
            }}
        >
            {t('timecards.finish')}
        </Button>
    );

    return (
        <div className="border-divider-soft flex min-h-[46px] items-center gap-2.5 border-b py-1">
            <ClosedCaseDraftMarker timecard={timecard} className="shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col">
                <LinkWithOrigin
                    to={`/timecards/${timecard.id}`}
                    className="text-foreground hover:text-link block min-w-0 font-medium no-underline hover:underline"
                >
                    <EllipsisTooltip>{matter.name}</EllipsisTooltip>
                </LinkWithOrigin>
                <span className="text-muted-foreground text-xs">
                    {format(dateStringToDateObject(timecard.timecardDate), t('timecards.list.group-date-format'))}
                    {' · '}
                    {formatToHours(timecard.timecardStats?.totalTimeInHours, displayDecimalScale)}
                    {' · '}
                    <span className="font-mono">
                        <Unmasked>{matter.caseNumber}</Unmasked>
                    </span>
                </span>
            </div>
            {matter.isCurrentlyBlocked ? (
                <BlockedTag />
            ) : showsClosedCaseInfo ? (
                <InfoTooltip text="timecards.tooltips.connected-case-closed" color="primary" />
            ) : canFinish && reason ? (
                <>
                    <Tooltip content={reason}>{finish}</Tooltip>
                    <span id={reasonId} hidden>
                        {reason}
                    </span>
                </>
            ) : canFinish ? (
                finish
            ) : null}
        </div>
    );
};

type Props = {
    /** Home's list: compact rows with Finish, no count header, and Show more. */
    home?: boolean;
};

const MyDraftTimecards: FC<Props> = ({ home = false }) => {
    const { user } = useAuth();
    const userId = user?.id;
    const [fetchData, { data, isFetching, isError }] = useLazyGetTimecardsWithFiltersQuery();
    const navigateWithReturn = useNavigateWithReturnState();

    const fetchPage = useCallback(
        (page: number) => {
            if (!userId) return;
            return fetchData({
                pageSize: PAGE_SIZE,
                page,
                sort: ['timecardDate,desc', 'stopwatchRunningSince,desc'],
                filters: myDraftTimecardsFilter(userId),
            });
        },
        [fetchData, userId]
    );

    const { handleLoadMore } = useLoadMoreFeed({ fetchPage, isLast: data?.last });

    const handleRetry = () => {
        void fetchPage(0);
    };

    const draftCount = data?.totalElements || 0;

    return (
        <div className="flex flex-col">
            {home ? null : (
                <TabContentHeader
                    leftContent={
                        <p className="text-body1">
                            <FormattedMessage
                                id="dashboard.n-draft-timecards"
                                values={{
                                    count: draftCount,
                                    element: (
                                        <span key="element" className="font-semibold">
                                            {draftCount}
                                        </span>
                                    ),
                                }}
                            />
                        </p>
                    }
                    rightContent={
                        <p className="text-body1 leading-6 font-semibold">
                            <FormattedMessage id="time.all-time" />
                        </p>
                    }
                />
            )}
            <div>
                {/* Order matters: a failed request and a request still in flight must NOT read
                    as tenant-empty. `!data` covers the frame before the mount effect fires the
                    lazy query, where isFetching is still false. */}
                {!data?.content.length && isError ? (
                    <EmptyState
                        size="panel"
                        variant="error"
                        onRetry={handleRetry}
                        surface="dashboard-draft-timecards"
                    />
                ) : (isFetching || !data) && !data?.content.length ? (
                    <GridLoader height={120} numberOfRows={5} />
                ) : !data?.content.length ? (
                    // Used to fall straight through to the list component with an empty array, which
                    // renders nothing at all — a loaded-but-empty card was blank below its header.
                    <EmptyState
                        size="panel"
                        icon={MoreTimeOutlinedIcon}
                        titleId="dashboard.empty.draft-timecards-title"
                        bodyId="dashboard.empty.draft-timecards-body"
                        surface="dashboard-draft-timecards"
                        action={
                            <PermissionButton
                                permissions={['CREATE.TIMECARDS']}
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={() => navigateWithReturn('/timecards/new')}
                            >
                                <FormattedMessage id="timecards.add-new-timecard" />
                            </PermissionButton>
                        }
                    />
                ) : home ? (
                    data.content.map((timecard) => (
                        <HomeDraftRow key={timecard.id} timecard={timecard} userId={userId} />
                    ))
                ) : (
                    <TimecardList timecards={data?.content || []} compact borderless />
                )}
                {data && !data.last ? (
                    home ? (
                        <HomeShowMore onClick={handleLoadMore} loading={isFetching} />
                    ) : (
                        <LoadMoreButton onClick={handleLoadMore} loading={isFetching} />
                    )
                ) : null}
            </div>
        </div>
    );
};

export default MyDraftTimecards;
